// SEND TO PROCORE / ACC (owner, 2026-10-23: "add a button on each shopdrawing
// row to upload to procore & ACC, checks if both connected and have
// shopdrawings path set ... popup showing procore and acc, checkboxes to decide
// 1 or both" - and the same for the ticked drawings in bulk).
//
// ONE COMPONENT FOR BOTH: a row passes one id and gets an icon button, the bulk
// bar passes the ticked ids and gets a labelled one. Its own file and state,
// because the page sits at the hooks lint's path limit (as BulkDownloadButton).
//
// THE POPUP ASKS THE SERVER WHICH BOXES CAN BE TICKED
// (GET /shop-drawings/external-targets): a platform that is not connected, has
// no shop drawings folder, or that this person may not write to is shown
// unticked and disabled WITH the reason, rather than hidden - a missing box
// reads as "this project has no ACC", which is not what is true.
//
// SEVERAL DESTINATIONS (owner 2026-10-24): when the server lists `destinations`, there is
// one box per destination ("Procore 1", "Procore 2 - Client", "ACC") and the send names
// them; otherwise one per platform, as before.
import { openFolderPaths } from "components/Integrations/FolderPathsPanel";
import React, { useState } from "react";
import { Button, Checkbox, Modal, Spin, Tooltip, message } from "antd";
import { CloudUploadOutlined } from "@ant-design/icons";
import { getShopDrawingExternalTargets, sendShopDrawingsExternal } from "api/tracking/shopDrawingsApi";
import { SENT_EVENT } from "components/Integrations/ShopDrawingExternalDelivery";

const PROVIDERS = ["procore", "acc"];
const NAME = { procore: "Procore", acc: "ACC" };

/** The message after a send: what was queued, and every reason something was not. */
export function sendMessage(r) {
  const results = (r && r.results) || [];
  const queued = results.filter((x) => x.queued);
  const per = PROVIDERS.map((p) => {
    const n = queued.filter((x) => x.provider === p).length;
    return n ? `${n} to ${NAME[p]}` : "";
  }).filter(Boolean);
  // A reason said once per platform and reason, with how many it covers.
  const reasons = new Map();
  for (const x of results) {
    if (!x.skipped) continue;
    const k = `${NAME[x.provider] || x.provider}: ${x.skipped}`;
    reasons.set(k, (reasons.get(k) || 0) + (x.count || 1));
  }
  const notSent = [...reasons.entries()].map(([k, n]) => `${k}${n > 1 ? ` (${n})` : ""}`);
  if (!queued.length) return { level: "warning", text: `Nothing sent. ${notSent.join(". ")}` };
  const text = `Uploading ${per.join(" and ")} - each drawing's current revision, into the shop drawings folder.`;
  return notSent.length ? { level: "warning", text: `${text} Not sent: ${notSent.join(". ")}` } : { level: "success", text };
}

// the popup's boxes: one per destination when the server lists them, else per platform
export function boxesOf(t) {
  if (t && Array.isArray(t.destinations) && t.destinations.length) {
    return t.destinations.map((d) => ({ id: d.id, provider: d.provider, label: d.name || NAME[d.provider], ready: !!d.ready, path: d.path, reason: d.reason }));
  }
  return PROVIDERS.map((p) => ({ id: p, provider: p, label: NAME[p], ready: !!(t && t[p] && t[p].ready), path: t && t[p] && t[p].path, reason: t && t[p] && t[p].reason }));
}

export default function SendExternalButton({ projectId, ids, row = false }) {
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const openIt = async () => {
    setOpen(true);
    setTargets(null);
    setLoading(true);
    try {
      const t = await getShopDrawingExternalTargets(projectId);
      setTargets(t);
      // Every platform that can take them starts ticked - "1 or both" is
      // unticking one, not ticking both.
      setChosen(boxesOf(t).filter((b) => b.ready).map((b) => b.id));
    } catch (e) {
      message.error(e.message || "Could not read the Procore / ACC settings");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const go = async () => {
    setBusy(true);
    try {
      const many = !!(targets && Array.isArray(targets.destinations) && targets.destinations.length);
      const r = many ? await sendShopDrawingsExternal(projectId, ids, [], chosen) : await sendShopDrawingsExternal(projectId, ids, chosen);
      const m = sendMessage(r);
      message[m.level](m.text, m.level === "warning" ? 10 : 5);
      setOpen(false);
      // the top bar's spinner and "Uploading 0 of N sheets" start now
      window.dispatchEvent(new Event(SENT_EVENT));
    } catch (e) {
      message.error(e.message || "Could not send to Procore / ACC");
    } finally {
      setBusy(false);
    }
  };

  if (!ids || !ids.length) return null;
  const count = ids.length;
  const what = `${count.toLocaleString()} shop drawing${count === 1 ? "" : "s"}`;
  return (
    <>
      {row ? (
        <Tooltip title="Upload to Procore / ACC - into the project's shop drawings folder">
          <Button size="small" icon={<CloudUploadOutlined />} onClick={openIt} data-testid="sd-send-external-row" />
        </Tooltip>
      ) : (
        <Button size="small" icon={<CloudUploadOutlined />} onClick={openIt} data-testid="sd-send-external">
          Upload {count.toLocaleString()} to Procore / ACC
        </Button>
      )}
      <Modal
        open={open}
        title={`Upload ${what} to Procore / ACC`}
        okText="Upload"
        okButtonProps={{ loading: busy, disabled: !chosen.length, "data-testid": "sd-send-external-go" }}
        onOk={go}
        onCancel={() => { if (!busy) setOpen(false); }}
        destroyOnClose
      >
        {loading || !targets ? <Spin /> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {boxesOf(targets).map((t) => (
              <div key={t.id}>
                <Checkbox
                  checked={chosen.includes(t.id)}
                  disabled={!t.ready}
                  onChange={(e) => setChosen((c) => (e.target.checked ? [...new Set([...c, t.id])] : c.filter((x) => x !== t.id)))}
                  data-testid={`sd-send-${t.id}`}
                >
                  <b>{t.label}</b>
                </Checkbox>
                <div style={{ color: t.ready ? "#667085" : "#b42318", fontSize: 12, marginLeft: 24 }} data-testid={`sd-send-${t.id}-note`}>
                  {t.ready ? `Into ${t.path || "the shop drawings folder"}` : `Cannot upload: ${t.reason || "not set up"}`}
                  {!t.ready && /folder/.test(t.reason || "") ? (
                    <button className="integration-link-btn" onClick={() => openFolderPaths(projectId)} data-testid={`sd-send-${t.id}-set-folder`}>
                      Set the shop drawings upload folder
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
            <div style={{ color: "#667085", fontSize: 12 }}>
              Each drawing's current revision is uploaded under its sheet number and name, so a new revision lands as the next version of the same file. A drawing with nothing delivered yet is skipped and listed.
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
